import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Heading } from '~/components';
import { EditableRowAdditionalDiscount } from '~/pages/Portal/Settings/Discounts/EditableRowAdditionalDiscount';
import { EditableRowDefaultDiscount } from '~/pages/Portal/Settings/Discounts/EditableRowDefaultDiscount';
import {
  useAdditionalDiscounts,
  useDefaultDiscounts,
} from '~/hooks/useDiscounts/useDiscounts';
import { AdditionalDiscount, DefaultDiscount } from '~/types/discount';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';

export const Discount: React.VFC = () => {
  const [isAdditionalDiscountBlocked, setAdditionalDiscountBlocked] =
    useState(false);
  const toast = useToast();
  const { data, isLoading, isFetching, refetch, isError } =
    useAdditionalDiscounts();
  const [additionalDiscountList, setAdditionalDiscountList] = useState<
    AdditionalDiscount[]
  >([]);

  const {
    data: defaultData,
    isLoading: defaultIsLoading,
    isFetching: defaultIsFetching,
    refetch: defaultRefetch,
    isError: defaultIsError,
  } = useDefaultDiscounts();
  const [defaultDiscountList, setDefaultDiscountList] = useState<
    DefaultDiscount[]
  >([]);

  const handleDeleteAdditionalDiscount = (id: number | undefined) => {
    if (id) {
      api.delete(`desconto_adicional/${id}`).then(() => {
        queryClient.invalidateQueries('additional-discounts');
        toast({
          title: 'Desconto removido',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
    } else {
      setAdditionalDiscountList(additionalDiscountList.filter(item => item.id));
      setAdditionalDiscountBlocked(false);
    }
  };

  const handleDeleteDefaultDiscount = (id: string | undefined) => {
    if (id?.length !== 36) {
      api.delete(`desconto_padrao/${id}`).then(() => {
        queryClient.invalidateQueries('default-discounts');
        toast({
          title: 'Desconto removido',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
    } else {
      setDefaultDiscountList(
        defaultDiscountList.filter(item => item.id?.toString() !== id),
      );
    }
  };

  useEffect(() => {
    setAdditionalDiscountList(data?.results || []);
    setDefaultDiscountList(defaultData?.results || []);
    setAdditionalDiscountBlocked(false);
  }, [data, defaultData]);

  const handleNewRowAdditionalList = () => {
    api
      .get('desconto_adicional/desconto_de/')
      .then(res => {
        setAdditionalDiscountBlocked(true);
        setAdditionalDiscountList([
          ...additionalDiscountList,
          {
            id: '',
            de: res.data.message || '',
            ate: '',
            desconto: '',
          } as unknown as AdditionalDiscount,
        ]);
      })
      .catch(err => {
        toast({
          description: `${err.response.data.message}`,
          title: 'Não foi possível adicionar uma nova linha.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const handleNewRowDefaultList = () => {
    setDefaultDiscountList([
      ...defaultDiscountList,
      {
        id: uuid(),
        tempoContrato: '',
        tempoExtensao: '',
        descontoPadrao: '',
        descontoMinimo: '',
        descontoMaximo: '',
      } as unknown as DefaultDiscount,
    ]);
  };

  return (
    <>
      <Stack gridGap="4" px="8" py="4">
        <Heading>Configurações de Descontos</Heading>
        <Box
          border="1px solid #f1f1f2"
          padding="10px"
          borderRadius="md"
          maxW={900}
          w="100%"
        >
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            padding="10px"
          >
            <Heading fontSize="md" mb={0}>
              Prazo de desconto adicional
            </Heading>
            <Tooltip
              label={
                isAdditionalDiscountBlocked
                  ? 'Preencha a última linha'
                  : 'Adicionar desconto'
              }
              shouldWrapChildren
            >
              <Button
                variant="outline"
                border="1px solid"
                borderColor="#939291"
                onClick={() => handleNewRowAdditionalList()}
                disabled={isAdditionalDiscountBlocked}
              >
                Adicionar
              </Button>
            </Tooltip>
          </Flex>
          {additionalDiscountList.length && !isLoading ? (
            additionalDiscountList.map((discount, index) => (
              <EditableRowAdditionalDiscount
                item={discount}
                handleDelete={handleDeleteAdditionalDiscount}
                key={discount.id || index + 1}
              />
            ))
          ) : (
            <Flex justify="center">
              <Text>Não foi encontrado nenhum desconto.</Text>
            </Flex>
          )}
          {additionalDiscountList.length > 0 && (
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          )}
        </Box>
        <Box
          border="1px solid #f1f1f2"
          padding="10px"
          borderRadius="md"
          maxW={900}
          w="100%"
        >
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            padding="10px"
          >
            <Heading fontSize="md" mb={0}>
              Prazo de desconto padrão
            </Heading>
            <Button
              variant="outline"
              border="1px solid"
              borderColor="#939291"
              onClick={() => handleNewRowDefaultList()}
            >
              Adicionar
            </Button>
          </Flex>
          {defaultDiscountList.length && !defaultIsLoading ? (
            defaultDiscountList.map((discount, index) => (
              <EditableRowDefaultDiscount
                item={discount}
                handleDelete={handleDeleteDefaultDiscount}
                key={discount.id || index + 1}
              />
            ))
          ) : (
            <Flex justify="center">
              <Text>Não foi encontrado nenhum desconto.</Text>
            </Flex>
          )}
          {defaultDiscountList.length > 0 && (
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          )}
        </Box>
      </Stack>
    </>
  );
};
